import * as React from 'react';
import { cn } from '../../lib/utils';

export interface SectionHeaderProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  /** Section title — smaller than PageHeader; lives inline within a page. */
  title: React.ReactNode;
  /** Optional one-line description. */
  description?: React.ReactNode;
  /** Right-aligned action cluster. */
  actions?: React.ReactNode;
  /** Render a divider above the section. */
  withDivider?: boolean;
}

/**
 * SectionHeader — inline header for a sub-section of a page. Visually
 * smaller than PageHeader; sits flush above the section's content.
 */
export const SectionHeader = React.forwardRef<
  HTMLDivElement,
  SectionHeaderProps
>(
  (
    { title, description, actions, withDivider, className, ...props },
    ref,
  ) => {
    return (
      <div
        ref={ref}
        className={cn(
          'flex flex-col gap-2 pb-3 md:flex-row md:items-start md:justify-between md:gap-4',
          withDivider && 'border-t border-border pt-4',
          className,
        )}
        {...props}
      >
        <div className="min-w-0">
          <h2 className="text-base font-semibold tracking-tight text-foreground">
            {title}
          </h2>
          {description && (
            <p className="mt-0.5 text-sm text-muted-foreground">
              {description}
            </p>
          )}
        </div>
        {actions && (
          <div className="flex shrink-0 items-center gap-2">{actions}</div>
        )}
      </div>
    );
  },
);
SectionHeader.displayName = 'SectionHeader';
